import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import { withErrorConsumed } from './common-utils';

const ATHENA_AUTHORIZE_COOKIE_KEY = 'athena-authorizeUrl';
const OHSW_AUTHORIZE_URL = 'athena-ohsw-authorizeUrl';
const STATE_TOKEN_OVERRIDE = 'state-token-override';
export const LOGIN_VIA_OHSW = 'LOGIN_VIA_OHSW';
const OHSW_SQTOKEN_KEY = 'OHSW_SQTOKEN';
const OHSW_PRIMARY_AUTH_PLUS_CUSTOM_OKTA_TXN_KEY = 'OHSW_PRIMARY_AUTH_PLUS_CUSTOM_OKTA_TXN';
const ATHENAHEALTH_COOKIE_DOMAIN = '.athenahealth.com';
const SMS_MANUAL_SETUP_PHONE_CODE = 'athena-sms-manual-setup-phone-code';
const STATE_TOKEN_EXPIRY_BANNER_TYPE = 'state-token-expiry-banner-type';
const TERMS_AND_CONDITIONS_SHOWN_KEY = 'tAndCShown';
const COOKIE_OPTIONS = {
  domain: ATHENAHEALTH_COOKIE_DOMAIN,
  path: '/',
};
const LWA_UNIQ_ID_COOKIE = 'lwa_uniq_id_spa';
const MINUTE_MILLISECONDS = 60 * 1000;
const DAY_MILLISECONDS = 24 * 60 * MINUTE_MILLISECONDS;




const crossDomainCookieOptions = () => {
  return { ...COOKIE_OPTIONS, domain: '.athenahealth.com', expires: new Date(Date.now() + MINUTE_MILLISECONDS * 10) };
};
/**
 * The storage interactions are wrapped in "ErrorConsumed" function, so it becomes no-op when storage is unavailable
 * (e.g. when cookies are disabled)
 */

export const getLwaAuthorizeUrl = withErrorConsumed(function () {
  //This cookie is expected to be set by LWA SPA
  return Cookies.get(ATHENA_AUTHORIZE_COOKIE_KEY) as string;
});
export const setInitialLaunchUrl = withErrorConsumed((url: string) => {
  sessionStorage.setItem(OHSW_AUTHORIZE_URL, url);
});

export const getInitialLaunchUrl = withErrorConsumed(() => {
  return sessionStorage.getItem(OHSW_AUTHORIZE_URL);
});

export const removeOhswAuthorizeUrl = withErrorConsumed(() => {
  return sessionStorage.removeItem(OHSW_AUTHORIZE_URL);
});

export const getOhswBannerKey = withErrorConsumed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const showBanner = urlParams.get('SHOWBANNER');
  const bannerTime = urlParams.get('BANNERTIME');
  const currentTime = Math.round(new Date().getTime() / 1000);
  if (bannerTime && showBanner && currentTime - parseInt(bannerTime) < 300) {
    return showBanner;
  }
});

export interface SqPromptCookiesArgsData {
  sqtoken: string;
  primaryAuthWithCustomTxn?: boolean;
}

export const setSecurityQuestionsPromptCookies = withErrorConsumed((sqPromptCookiesData: SqPromptCookiesArgsData) => {
  Cookies.set(OHSW_SQTOKEN_KEY, sqPromptCookiesData.sqtoken, COOKIE_OPTIONS);
  if (sqPromptCookiesData.primaryAuthWithCustomTxn) {
    Cookies.set(
      OHSW_PRIMARY_AUTH_PLUS_CUSTOM_OKTA_TXN_KEY,
      sqPromptCookiesData.primaryAuthWithCustomTxn ? 'Y' : 'N',
      COOKIE_OPTIONS
    );
  }
});

export const clearSecurityQuestionsPromptCookies = withErrorConsumed(() => {
  const options = {
    domain: ATHENAHEALTH_COOKIE_DOMAIN,
    path: '/',
  };
  Cookies.remove(OHSW_SQTOKEN_KEY, options);
  Cookies.remove(OHSW_PRIMARY_AUTH_PLUS_CUSTOM_OKTA_TXN_KEY, options);
});

export const isSecurityQuestionsTokenCookieSet = withErrorConsumed(() => {
  return !!Cookies.get(OHSW_SQTOKEN_KEY);
});

export const isLoginViaOhswCookieSet = withErrorConsumed(function () {
  return Cookies.get(LOGIN_VIA_OHSW) === 'ON';
});

export const getShowBannerValue = withErrorConsumed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('SHOWBANNER') || '';
});

export const getStateTokenOverride = withErrorConsumed(() => {
  return sessionStorage.getItem(STATE_TOKEN_OVERRIDE);
});

export const setStateTokenOverride = withErrorConsumed((stateToken: string) => {
  return sessionStorage.setItem(STATE_TOKEN_OVERRIDE, stateToken);
});

export const removeStateTokenOverride = withErrorConsumed(() => {
  return sessionStorage.removeItem(STATE_TOKEN_OVERRIDE);
});

export const setSmsManualSetupPhoneCode = withErrorConsumed((phoneNumber: string) => {
  return sessionStorage.setItem(SMS_MANUAL_SETUP_PHONE_CODE, phoneNumber);
});

export const getSmsManualSetupPhoneCode = withErrorConsumed(() => {
  return sessionStorage.getItem(SMS_MANUAL_SETUP_PHONE_CODE);
});

export const removeSmsManualSetupPhoneCode = withErrorConsumed(() => {
  return sessionStorage.removeItem(SMS_MANUAL_SETUP_PHONE_CODE);
});

export const setStateTokenExpiryBannerType = withErrorConsumed((bannerType: string) => {
  return sessionStorage.setItem(STATE_TOKEN_EXPIRY_BANNER_TYPE, bannerType);
});

export const getStateTokenExpiryBannerType = withErrorConsumed(() => {
  return sessionStorage.getItem(STATE_TOKEN_EXPIRY_BANNER_TYPE);
});

export const removeStateTokenExpiryBannerType = withErrorConsumed(() => {
  return sessionStorage.removeItem(STATE_TOKEN_EXPIRY_BANNER_TYPE);
});

export const clearStorage = withErrorConsumed(() => {
  Cookies.remove(ATHENA_AUTHORIZE_COOKIE_KEY);
  removeSmsManualSetupPhoneCode();
});

export const removeStateHandle = withErrorConsumed(() => {
  sessionStorage.removeItem('osw-oie-state-handle');
  sessionStorage.removeItem('osw-oie-last-initiated-login-url');
});

export function setAndGetLwaUniqId() {
  let lwaUniqId = Cookies.get(LWA_UNIQ_ID_COOKIE);
  if (!lwaUniqId) {
    lwaUniqId = nanoid();
    // eslint-disable-next-line max-len
    Cookies.set(LWA_UNIQ_ID_COOKIE, lwaUniqId, { ...crossDomainCookieOptions(), expires: new Date(Date.now() + (DAY_MILLISECONDS * 7)) });
  }
  return lwaUniqId;
}

export const getAuthorizeUrl = () => new URL(getLwaAuthorizeUrl() || getInitialLaunchUrl() || window.location.href);

export const setTermsAndConditionsShownCookie = withErrorConsumed(() => {
  Cookies.set(TERMS_AND_CONDITIONS_SHOWN_KEY, 'true', { ...crossDomainCookieOptions(), expires: undefined });
});
